/**
 * Aviso del analizador del respaldo por empresa (adaptado de K360): alguna base cambió y
 * hay tablas que no entran en el respaldo por empresa, o reglas rotas. UN botón lo
 * resuelve: el servidor ya decidió qué hacer con cada tabla (por sus llaves foráneas o el
 * nombre de sus columnas) y la clasificación queda guardada para todas las cuentas.
 */
import React, { useState } from 'react';
import { ShieldAlert, ShieldCheck, RefreshCw, ChevronDown, Loader2 } from 'lucide-react';
import { respaldosApi } from '../utils/api';

const ETIQUETA = {
  hija: 'se agregan al respaldo por empresa',
  cuenta: 'se marcan de la cuenta (compartidas, no van por empresa)',
  excluida: 'se excluyen (no tienen columna id)',
  quitar: 'se les quita una regla vieja'
};

export default function AvisoRespaldoEmpresa({ analisis, onCambio, onAviso }) {
  const [ocupado, setOcupado] = useState(false);
  const [detalle, setDetalle] = useState(false);
  if (!analisis) return null;

  if (analisis.ok) {
    return (
      <div className="mb-4 flex items-center gap-2 px-4 py-2.5 rounded-lg border border-emerald-200 bg-emerald-50 text-sm text-emerald-800">
        <ShieldCheck className="h-4 w-4 shrink-0" />
        Respaldo por empresa al día: {analisis.tablas} tablas en {analisis.cuentas} cuenta(s), todas clasificadas.
        {analisis.avisos > 0 && <span className="text-amber-700">{analisis.avisos} aviso(s).</span>}
      </div>
    );
  }

  const r = analisis.resumen || {};
  const conSolucion = ['hija', 'cuenta', 'excluida', 'quitar'].filter(k => r[k]?.length);
  const sinSolucion = analisis.alertas.filter(x => !x.recomendacion && x.nivel === 'error');

  const actualizar = async () => {
    setOcupado(true);
    try {
      const res = await respaldosApi.aplicarAnalisis();
      const n = res.data.aplicadas.length;
      onAviso?.({ tipo: 'ok', texto: n ? `Respaldo por empresa actualizado: ${n} tabla(s). El próximo respaldo ya las incluye.` : 'No había nada que aplicar.' });
      if (res.data.sinSolucion?.some(x => x.nivel === 'error')) {
        onAviso?.({ tipo: 'error', texto: `${res.data.sinSolucion.filter(x => x.nivel === 'error').length} problema(s) requieren un cambio en el código.` });
      }
      onCambio?.(res.data.analisis);
    } catch (e) {
      onAviso?.({ tipo: 'error', texto: e.response?.data?.error || 'No se pudo actualizar el respaldo por empresa' });
    } finally {
      setOcupado(false);
    }
  };

  return (
    <div className="mb-6 p-4 rounded-xl border border-red-300 bg-red-50">
      <div className="flex flex-wrap items-start gap-4">
        <ShieldAlert className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
        <div className="text-sm flex-1 min-w-[16rem]">
          <p className="font-semibold text-red-800">
            Una base cambió: {analisis.errores} tabla(s) o regla(s) no entran en el respaldo por empresa.
          </p>
          <p className="text-red-700 mt-0.5">Hasta actualizarlo, restaurar una empresa la dejaría inconsistente. Al actualizar:</p>
          <ul className="mt-1 text-red-800 space-y-0.5">
            {conSolucion.map(k => <li key={k}>· <strong>{r[k].length}</strong> {ETIQUETA[k]}: <code>{r[k].join(', ')}</code></li>)}
            {r.enCadena?.length > 0 && <li>· <strong>{r.enCadena.length}</strong> cuelgan de otra tabla nueva y se clasifican después de esa: <code>{r.enCadena.join(', ')}</code></li>}
            {r.revisar?.length > 0 && <li className="text-amber-800">· <strong>Revisar después</strong> (se clasifican, pero el analizador no está seguro): <code>{r.revisar.join(', ')}</code></li>}
            {sinSolucion.length > 0 && <li>· <strong>{sinSolucion.length}</strong> requieren un cambio en el código: <code>{[...new Set(sinSolucion.map(x => x.tabla))].join(', ')}</code></li>}
            {analisis.erroresLectura?.length > 0 && <li>· No se pudo leer: <code>{analisis.erroresLectura.map(x => x.cuenta).join(', ')}</code></li>}
          </ul>
          <button type="button" onClick={() => setDetalle(!detalle)} className="mt-1 text-xs text-red-700 hover:underline inline-flex items-center gap-1">
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${detalle ? 'rotate-180' : ''}`} />{detalle ? 'Ocultar detalle' : 'Ver detalle'}
          </button>
          {detalle && (
            <ul className="mt-2 space-y-1.5 text-xs text-gray-700 bg-white rounded-lg p-3 border border-red-100">
              {analisis.alertas.map(x => (
                <li key={`${x.tabla}-${x.mensaje}`}>
                  <code className="font-semibold">{x.tabla}</code>{x.cuentas?.length ? <span className="text-gray-500"> ({x.cuentas.join(', ')})</span> : null}: {x.mensaje}{' '}
                  <span className="text-red-700">→ {x.recomendacion ? x.recomendacion.etiqueta : x.sugerencia}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        {conSolucion.length > 0 && (
          <button type="button" onClick={actualizar} disabled={ocupado}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-red-600 text-red-700 hover:bg-red-100 font-medium disabled:opacity-50 shrink-0">
            {ocupado ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Actualizar el respaldo por empresa
          </button>
        )}
      </div>
    </div>
  );
}
